import { useEffect, useRef, type FormEvent, type ReactNode } from 'react';

/** The mobile app's bottom sheets as a modal dialog: a bottom sheet on narrow screens, a centered
 * dialog on wide ones. Escape and a click outside close it. */
export function Sheet({
  open,
  onClose,
  title,
  children,
  actions,
  onSubmit,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** Makes the sheet a form: Enter in a field submits it. */
  onSubmit?: () => void;
  labelledBy?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  // Phones: the on-screen keyboard covers the bottom of the page without making it shorter, so the
  // sheet moves up by what the keyboard covers (--keyboard, theme.css).
  useEffect(() => {
    const d = ref.current;
    const vv = window.visualViewport;
    if (!open || !d || !vv) return;
    const update = () => {
      const covered = Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop));
      d.style.setProperty('--keyboard', `${covered}px`);
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      d.style.removeProperty('--keyboard');
    };
  }, [open]);

  const body = (
    <div className="sheet-body">
      {title && (
        <h2 id={labelledBy} className="headline-small">
          {title}
        </h2>
      )}
      {children}
      {actions && <div className="sheet-actions">{actions}</div>}
    </div>
  );

  return (
    <dialog
      ref={ref}
      className="sheet"
      aria-labelledby={labelledBy}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      {open &&
        (onSubmit ? (
          <form
            method="dialog"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              onSubmit();
            }}
          >
            {body}
          </form>
        ) : (
          body
        ))}
    </dialog>
  );
}
